'use client'

import Link from '@components/primitives/Link'
import { Text } from '@components/primitives/Text'
import {
  faCalendar,
  faClipboardListCheck,
  faUserLock,
} from '@fortawesome/pro-regular-svg-icons'
import Routes from '@lib/routes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import type { TrialInAlgolia } from '@modules/trials/types/TrialInAlgolia'
import { useLongPress } from '@uidotdev/usehooks'
import { useEffect } from 'react'
import { BottomNavContainer } from '../ApplySuccessPage/PostApplyPrompt/BottomNavContainer'
import { TrialApplyStartValueProp } from '../Questionnaire/TrialApplicationQuestionnaire/TrialApplyStartValueProp'
import TrialCard from '../TrialCard'
import {
  ApplyFlowSection,
  ApplyFlowSectionScore,
  trackApplyFlowStepViewed,
} from './trackApplyFlowStepViewed'
import TrialApplyQuestionContainer from './TrialApplyQuestionContainer.client'
import TrialApplyStartButton from './TrialApplyStartButton.client'

export default function TrialApplyStart({ trial }: { trial: TrialInAlgolia }) {
  const longPressProps = useLongPress(
    () => {
      window.open(absoluteUrl(Routes.termsOfService))
    },
    { threshold: 500 },
  )

  useEffect(() => {
    const stepNumber = 1
    trackApplyFlowStepViewed({
      applyFlowOverallNumber:
        ApplyFlowSectionScore[ApplyFlowSection.TRIAL_APPLY] + stepNumber,
      applyFlowQuestionText: 'Contact this trial',
      applyFlowSectionStepNumber: stepNumber,
      applyFlowSectionTitle: ApplyFlowSection.TRIAL_APPLY,
    })
  }, [])

  return (
    <>
      <TrialApplyQuestionContainer>
        <div className='mb-2 md:mb-0'>
          <Text
            className='text-4xl'
            styleName='h3'
            value='Contact this trial'
          />
          <Text
            className='text-neutral700'
            value='First, we need to learn more about you.'
          />
        </div>
        <div className='flex gap-10'>
          <div className='hidden w-5/12 md:inline-block'>
            <TrialCard
              hideButtomButton={true}
              nearestPoint={undefined}
              trial={trial}
            />
          </div>
          <div className='flex flex-col gap-8 overflow-y-auto pb-16 md:w-7/12 md:pb-0'>
            <TrialApplyStartValueProp
              icon={faClipboardListCheck}
              subtitle='Save answers to your profile & unlock recommendations.'
              title='Fill-in a few details and create your profile'
            />
            {/* The links below are intentionally not underlined to reduce drop off */}
            <TrialApplyStartValueProp
              icon={faUserLock}
              subtitle={
                <>
                  {`We won't share unless you give us permission. By completing this form you agree to our`}{' '}
                  <Link href={absoluteUrl(Routes.privacyPolicy)} newTab>
                    privacy policy
                  </Link>
                  {` and `}{' '}
                  <Link href={absoluteUrl(Routes.termsOfService)} newTab>
                    terms of service
                  </Link>
                  .
                </>
              }
              title='We will keep your details private'
            />
            <TrialApplyStartValueProp
              icon={faCalendar}
              subtitle={`We’ll send your responses to the study team and help you to setup your first screening. This is not the start of informed consent. Screening happens between you and the trial.`}
              title='Get in touch with the study team'
            />
          </div>
        </div>
      </TrialApplyQuestionContainer>

      <BottomNavContainer
        progressBarProps={{
          currentStepIndex: 0,
          hideBackNavigation: true,
          numberOfSteps: 6,
          segment: 1,
        }}
      >
        <div className='flex w-full flex-col items-center justify-end gap-4 text-neutral500 md:w-2/3 md:flex-row'>
          <div className='flex flex-row gap-1'>
            <Text
              className='text-neutral500'
              element='p'
              styleName='p-xxtiny'
              value={'By clicking start, you agree to our '}
            />
            <Link
              className='hidden md:block'
              href={absoluteUrl(Routes.termsOfService)}
              newTab
            >
              <Text
                className='text-neutral500'
                element='p'
                styleName='p-xxtiny'
                value={'terms of service'}
              />
            </Link>
            {/* The long press to open may seem like an anti-pattern, but it's used to prevent
            accidental clicks near the "Apply Now" button on mobile that navigates users away from
            the flow, reducing drop-offs. */}
            <Text
              className='block text-neutral500 md:hidden'
              element='p'
              styleName='p-xxtiny'
              value={'terms of service'}
              {...longPressProps}
            />
          </div>
          <TrialApplyStartButton slug={trial.slug!} />
        </div>
      </BottomNavContainer>
    </>
  )
}
